import { useDrawersStore } from 'src/stores/useDrawersStore';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { createApyInfoAction } from 'src/components/Earn/utils/earnProductUtils';
import { useApyBreakdownTooltipData } from 'src/hooks/useApyBreakdownTooltipData';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { useAllWalletBalances } from 'src/hooks/query/walletBalances/useWalletBalance';
import NumLib from 'src/utils/NumLib';
import { EarnAccordionOptions } from 'src/hooks/useEarnProduct';

export function useEarnProductData(earnProduct: EarnAccordionOptions) {
	const cleanedMarketSymbol = getCleanedMarketSymbol(earnProduct.marketSymbol);

	const setDrawerStore = useDrawersStore((s) => s.set);
	const spotMarketLookup = useSpotMarketsLookup();

	// Add action handler to product data
	const apyInfoAction = createApyInfoAction(
		earnProduct.productType,
		earnProduct.marketIndex,
		setDrawerStore,
		spotMarketLookup
	);

	const finalProductData = {
		...earnProduct,
		apyInfoAction,
	};

	const apyData = useApyBreakdownTooltipData(finalProductData);

	const allWalletBalances = useAllWalletBalances();

	const balance = allWalletBalances.get(cleanedMarketSymbol);
	const shouldShowWalletBalance =
		Boolean(balance) &&
		((balance?.quote.toNum() ?? 0) >= 0.1 ||
			(balance?.base.toNum() ?? 0) > 0.0001);

	const getWalletBalance = (spotMarketSymbol: string): string => {
		const balance = allWalletBalances.get(spotMarketSymbol)?.base;
		if (!balance) return '0';

		const numericValue = balance.toNum();

		return NumLib.formatNum.toDecimalPlaces(numericValue, 4, true);
	};

	const apyStr =
		earnProduct.apyToShow !== undefined
			? earnProduct.productType === 'strategy_vaults' &&
				earnProduct.apyToShow > 30
				? '> 30%'
				: !isFinite(earnProduct.apyToShow) || earnProduct.apyToShow > 10000
					? '> 10,000%'
					: earnProduct.apyToShow < 0.01
						? '< 0.01%'
						: `${earnProduct.apyToShow.toFixed(2)}%`
			: '0%';

	return {
		cleanedMarketSymbol,
		finalProductData,
		apyData,
		shouldShowWalletBalance,
		getWalletBalance,
		apyStr,
	};
}
